// Bloc « Financement » de la fiche véhicule : comment ce camion est payé.
//
// Le contrat en cours EN PREMIER, puis l'historique. Un camion renouvelé deux
// fois a trois contrats, et c'est leur somme qui dit ce qu'il a coûté.
//
// UN SEUL CONTRAT ACTIF À LA FOIS. Le serveur répond 409 sinon, et l'écran ne
// propose donc pas d'en ajouter un second : on clôture, puis on saisit le
// suivant. L'ancien reste lisible.
import { useCallback, useEffect, useState } from "react";

import api from "../api";
import { ecartMoisJours, formaterDate } from "../format";
import ModaleConfirmation from "./ModaleConfirmation";

const CONTRATS = [
  { valeur: "credit_bail", label: "Crédit-bail" },
  { valeur: "loa", label: "LOA" },
  { valeur: "credit", label: "Crédit" },
  { valeur: "fonds_propres", label: "Fonds propres" },
];

const libelleContrat = (v) => CONTRATS.find((c) => c.valeur === v)?.label || v;

// Taux de TVA, POUR L'APERÇU DE SAISIE UNIQUEMENT.
//
// La référence est côté serveur (`schemas.TAUX_TVA`), et c'est lui qui renvoie
// les montants affichés une fois le contrat enregistré. Cette valeur-ci ne sert
// qu'à montrer le TTC pendant qu'on tape, avant tout aller-retour.
//
// Même principe que la table de `src/recurrences.js` : un repli d'affichage,
// jamais la référence. Si les deux divergent un jour, c'est le serveur qui a
// raison — et l'écart ne se verra que sur un champ en cours de saisie.
const TAUX_TVA = 0.2;

// Un achat comptant n'a ni loyer, ni fin, ni valeur résiduelle. Afficher ces
// champs inviterait à les remplir, et l'encours deviendrait faux.
const AVEC_ECHEANCIER = new Set(["credit_bail", "loa", "credit"]);

const NOUVEAU_VIDE = {
  type_contrat: "credit_bail",
  organisme: "",
  reference_contrat: "",
  date_debut: "",
  // Plus de `date_fin` : elle est CALCULEE par le serveur (debut + duree).
  // Un champ de saisie qui ne sert plus est un champ qu'on remplit quand meme,
  // et dont la valeur sera ecrasee sans que personne comprenne.
  duree_mois: "",
  loyer_mensuel: "",
  valeur_residuelle_pct: "",
  prix_achat: "",
  notes: "",
};

function nettoyer(brut) {
  const corps = {};
  Object.entries(brut).forEach(([cle, valeur]) => {
    if (valeur !== "" && valeur !== null && valeur !== undefined)
      corps[cle] = valeur;
  });
  return corps;
}

function euros(valeur) {
  if (valeur === null || valeur === undefined) return "—";
  return Number(valeur).toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });
}

export default function FinancementVehicule({ vehiculeId, estAdmin, onModification }) {
  // `onModification` PRÉVIENT L'ÉCRAN QUI NOUS CONTIENT.
  //
  // Sur la fiche véhicule, personne n'en a besoin. Sur l'écran Financement, si :
  // le tableau d'encours est affiché AU-DESSUS de ce bloc. Sans ce rappel, on
  // saisit un contrat, et le total juste au-dessus continue d'afficher
  // l'ancien chiffre — l'utilisateur croit que rien n'a été enregistré, et
  // ressaisit. Un écran qui ment sur ce qu'il vient de faire est pire qu'un
  // écran qui refuse.
  const [contrats, setContrats] = useState(null); // null = pas encore chargé
  const [erreur, setErreur] = useState("");
  const [ouvert, setOuvert] = useState(false);
  // Id du contrat en cours de CORRECTION, ou null pour une creation.
  //
  // Un contrat se saisit a la main depuis un papier : on se trompe d'un
  // chiffre, d'un mois. Avant, la seule issue etait de SUPPRIMER et de tout
  // ressaisir -- ce qui efface aussi la trace de ce qui existait, et pousse a
  // ne rien corriger du tout.
  const [edition, setEdition] = useState(null);
  const [nouveau, setNouveau] = useState(NOUVEAU_VIDE);
  const [enCours, setEnCours] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const charger = useCallback(() => {
    api
      .get(`/vehicules/${vehiculeId}/financements`)
      .then(({ data }) => setContrats(data))
      .catch(() => setErreur("Impossible de charger le financement."));
  }, [vehiculeId]);

  useEffect(charger, [charger]);

  const ajouter = (e) => {
    e.preventDefault();

    // ON RECAPITULE LES CHIFFRES, ON NE DEMANDE PAS « ETES-VOUS SUR ? ».
    //
    // Une question fermee s'apprend a valider sans lire : au troisieme
    // contrat, on clique « oui » avant d'avoir vu la fenetre. Redire les
    // montants, la duree et la fin CALCULEE oblige a comparer avec le papier
    // qu'on a sous les yeux -- c'est la seule relecture qui attrape une
    // erreur de saisie.
    //
    // C'est aussi le dernier moment ou la date de fin peut etre verifiee :
    // apres, elle ne se voit plus qu'en rouvrant le contrat.
    const lignes = [
      `Type       : ${libelleContrat(nouveau.type_contrat)}`,
      nouveau.organisme ? `Organisme  : ${nouveau.organisme}` : null,
      nouveau.prix_achat
        ? `Prix achat : ${euros(nouveau.prix_achat)} HT`
        : null,
      nouveau.loyer_mensuel
        ? `Loyer      : ${euros(nouveau.loyer_mensuel)} HT/mois`
        : null,
      nouveau.duree_mois ? `Durée      : ${nouveau.duree_mois} mois` : null,
      finCalculee ? `Fin        : ${finCalculee}` : null,
      vrEuros !== null
        ? `VR         : ${nouveau.valeur_residuelle_pct} % — ${euros(vrEuros)}`
        : null,
    ].filter(Boolean);

    setConfirmation({
      titre: edition ? "Enregistrer ces corrections ?" : "Enregistrer ce financement ?",
      message: lignes.join("\n"),
      libelleConfirmer: "Enregistrer",
      onConfirmer: async () => {
        setEnCours(true);
        setErreur("");
        try {
          if (edition) await api.put(`/financements/${edition}`, nettoyer(nouveau));
          else await api.post(`/vehicules/${vehiculeId}/financements`, nettoyer(nouveau));
          setNouveau(NOUVEAU_VIDE);
          setOuvert(false);
          setEdition(null);
          charger();
          onModification?.();
        } finally {
          setEnCours(false);
        }
      },
    });
  };

  /** Ouvre le formulaire pre-rempli avec un contrat existant.
   *
   * On repart des valeurs STOCKEES et non de ce qui est affiche : l'ecran
   * montre la VR en euros a cote du pourcentage, et le TTC a cote du HT --
   * recopier l'affichage ferait saisir un TTC dans un champ HT. */
  const corriger = (contrat) => {
    setNouveau({
      type_contrat: contrat.type_contrat || "credit_bail",
      organisme: contrat.organisme || "",
      reference_contrat: contrat.reference_contrat || "",
      date_debut: contrat.date_debut || "",
      duree_mois: contrat.duree_mois ?? "",
      loyer_mensuel: contrat.loyer_mensuel ?? "",
      valeur_residuelle_pct: contrat.valeur_residuelle_pct ?? "",
      prix_achat: contrat.prix_achat ?? "",
      notes: contrat.notes || "",
    });
    setEdition(contrat.id);
    setOuvert(true);
    setErreur("");
  };

  const cloturer = (contrat) => {
    setConfirmation({
      titre: "Clôturer ce contrat ?",
      sousTitre: libelleContrat(contrat.type_contrat),
      message: "Il sortira de l’encours mais restera dans l’historique du véhicule.",
      libelleConfirmer: "Clôturer",
      onConfirmer: async () => {
        await api.put(`/financements/${contrat.id}`, { actif: false });
        charger();
        onModification?.();
      },
    });
  };

  const supprimer = (contrat) => {
    setConfirmation({
      titre: "Supprimer définitivement ce contrat ?",
      sousTitre: libelleContrat(contrat.type_contrat),
      message:
        "L'historique de financement de ce véhicule perdra cette ligne — c'est "
        + "elle qui dit ce que le camion a coûté. Préférez « Clôturer » si le "
        + "contrat est simplement terminé.",
      libelleConfirmer: "Supprimer",
      danger: true,
      onConfirmer: async () => {
        await api.delete(`/financements/${contrat.id}`);
        charger();
        onModification?.();
      },
    });
  };

  const actif = (contrats || []).find((c) => c.actif);
  const anciens = (contrats || []).filter((c) => !c.actif);
  const echeancier = AVEC_ECHEANCIER.has(nouveau.type_contrat);

  // APERÇUS CALCULÉS PENDANT LA SAISIE.
  //
  // Le serveur reste la référence : c'est lui qui pose la date de fin et
  // déduit la valeur résiduelle en euros (voir `_poser_date_fin` et
  // `valeur_residuelle_euros`). Ce qui suit ne fait que MONTRER le résultat
  // avant d'enregistrer.
  //
  // Pourquoi le montrer : la date de fin déclenche l'alerte 90 jours avant, et
  // la valeur résiduelle est le chiffre qu'il faudra sortir pour racheter le
  // camion. Un calcul invisible ne se vérifie pas — le client doit pouvoir
  // reconnaître les deux sur son contrat papier avant de valider.
  const finCalculee = (() => {
    if (!nouveau.date_debut || !nouveau.duree_mois) return null;
    const mois = Number(nouveau.duree_mois);
    if (!Number.isFinite(mois) || mois <= 0) return null;
    const d = new Date(nouveau.date_debut);
    if (Number.isNaN(d.getTime())) return null;
    const jour = d.getDate();
    d.setMonth(d.getMonth() + mois);
    // Le 31 mai + 1 mois donne le 1er juillet en JavaScript : on ramène au
    // dernier jour du mois visé, comme le fait `recurrences.ajouter_mois`.
    if (d.getDate() < jour) d.setDate(0);
    return formaterDate(d.toISOString().slice(0, 10));
  })();

  const vrEuros =
    nouveau.prix_achat && nouveau.valeur_residuelle_pct
      ? (Number(nouveau.prix_achat) * Number(nouveau.valeur_residuelle_pct)) / 100
      : null;

  return (
    <section className="carte">
      {confirmation && (
        <ModaleConfirmation
          {...confirmation}
          onFermer={() => setConfirmation(null)}
        />
      )}
      <div className="entete-section">
        <h3>Financement</h3>
        {/* Pas de bouton quand un contrat tourne déjà : le serveur refuserait
            (409), et proposer une porte fermée passe pour un bug. */}
        {estAdmin && !actif && (
          <button
            className="btn"
            onClick={() => {
              setOuvert((v) => !v);
              setEdition(null);
              setNouveau(NOUVEAU_VIDE);
              setErreur("");
            }}
          >
            {ouvert ? "Annuler" : "+ Ajouter"}
          </button>
        )}
      </div>

      {erreur && <p className="erreur">{erreur}</p>}

      {ouvert && (
        <form className="formulaire" onSubmit={ajouter}>
          <div className="grille-form">
            <label className="champ">
              <span className="champ-label">Type de contrat *</span>
              <select
                value={nouveau.type_contrat}
                onChange={(e) =>
                  setNouveau({ ...nouveau, type_contrat: e.target.value })
                }
              >
                {CONTRATS.map((c) => (
                  <option key={c.valeur} value={c.valeur}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            {echeancier && (
              <label className="champ">
                <span className="champ-label">Organisme</span>
                <input
                  placeholder="Banque, loueur…"
                  value={nouveau.organisme}
                  onChange={(e) =>
                    setNouveau({ ...nouveau, organisme: e.target.value })
                  }
                />
              </label>
            )}
            <label className="champ">
              <span className="champ-label">Référence du contrat</span>
              <input
                value={nouveau.reference_contrat}
                onChange={(e) =>
                  setNouveau({ ...nouveau, reference_contrat: e.target.value })
                }
              />
            </label>
            <label className="champ">
              <span className="champ-label">Date de début</span>
              <input
                type="date"
                value={nouveau.date_debut}
                onChange={(e) =>
                  setNouveau({ ...nouveau, date_debut: e.target.value })
                }
              />
            </label>
            {echeancier && (
              <>
                <label className="champ">
                  <span className="champ-label">Durée (mois)</span>
                  <input
                    type="number"
                    min="1"
                    max="240"
                    step="1"
                    value={nouveau.duree_mois}
                    onChange={(e) =>
                      setNouveau({ ...nouveau, duree_mois: e.target.value })
                    }
                  />
                  {/* La date de fin se DEDUIT, et on la montre avant
                      d'enregistrer : c'est elle qui declenchera l'alerte
                      90 jours avant, et le client doit pouvoir la reconnaitre
                      sur son contrat. Un calcul invisible ne se verifie pas. */}
                  <span className="note-discrete">
                    {finCalculee
                      ? `Fin le ${finCalculee} — calculée`
                      : "Fin calculée à partir du début et de la durée"}
                  </span>
                </label>
                <label className="champ">
                  <span className="champ-label">Loyer mensuel HT (€)</span>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={nouveau.loyer_mensuel}
                    onChange={(e) =>
                      setNouveau({ ...nouveau, loyer_mensuel: e.target.value })
                    }
                  />
                  {/* Le montant reellement preleve chaque mois. Sans lui, le
                      client rapproche un HT d'un TTC sur son relevé bancaire
                      et croit a une erreur. */}
                  {nouveau.loyer_mensuel && (
                    <span className="note-discrete">
                      soit {euros(Number(nouveau.loyer_mensuel) * (1 + TAUX_TVA))} TTC
                    </span>
                  )}
                </label>
                <label className="champ">
                  <span className="champ-label">Valeur résiduelle (%)</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="any"
                    value={nouveau.valeur_residuelle_pct}
                    onChange={(e) =>
                      setNouveau({
                        ...nouveau,
                        valeur_residuelle_pct: e.target.value,
                      })
                    }
                  />
                  {/* Le pourcentage est ce qui figure au contrat ; les euros
                      sont ce qu'il faudra sortir pour racheter le camion. On
                      affiche les deux, pour n'obliger personne au calcul. */}
                  <span className="note-discrete">
                    {vrEuros !== null
                      ? `soit ${euros(vrEuros)} — % du prix d’achat`
                      : "en % du prix d’achat"}
                  </span>
                </label>
              </>
            )}
            <label className="champ">
              <span className="champ-label">Prix d’achat HT (€)</span>
              <input
                type="number"
                min="0"
                step="any"
                value={nouveau.prix_achat}
                onChange={(e) =>
                  setNouveau({ ...nouveau, prix_achat: e.target.value })
                }
              />
              {nouveau.prix_achat && (
                <span className="note-discrete">
                  TVA {euros(Number(nouveau.prix_achat) * TAUX_TVA)} — soit{" "}
                  {euros(Number(nouveau.prix_achat) * (1 + TAUX_TVA))} TTC
                </span>
              )}
            </label>
            <label className="champ champ-large">
              <span className="champ-label">Notes</span>
              <input
                value={nouveau.notes}
                onChange={(e) => setNouveau({ ...nouveau, notes: e.target.value })}
              />
            </label>
          </div>
          {echeancier && (
            <p className="sous-info">
              La <strong>date de fin</strong> et le <strong>loyer</strong>
              {" "}servent à calculer l’encours et à prévenir avant l’échéance,
              90 jours à l’avance. Sans eux, ce contrat n’apparaîtra dans aucun
              total et ne déclenchera aucune alerte.
            </p>
          )}
          <div className="actions-form">
            <button className="btn-accent" type="submit" disabled={enCours}>
              {enCours
                ? "Enregistrement…"
                : edition
                ? "Enregistrer les corrections"
                : "Enregistrer"}
            </button>
            {/* UNE SORTIE, TOUJOURS. En correction, le bouton « Annuler » de
                l'en-tête n'existe pas : il ne s'affiche que sur un véhicule
                SANS contrat actif. Sans celui-ci, on ouvrirait le formulaire
                sans pouvoir en sortir autrement qu'en enregistrant — ce qui
                pousse à valider une saisie qu'on voulait abandonner. */}
            {edition && (
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setOuvert(false);
                  setEdition(null);
                  setNouveau(NOUVEAU_VIDE);
                  setErreur("");
                }}
              >
                Annuler
              </button>
            )}
          </div>
        </form>
      )}

      {contrats === null ? (
        <p className="note-discrete">Chargement…</p>
      ) : contrats.length === 0 ? (
        <p className="vide">
          Aucun financement renseigné. Crédit-bail, LOA, crédit ou achat
          comptant : c’est ce qui dit combien ce camion coûte encore.
        </p>
      ) : (
        <>
          {actif && (
            <div className="grille-champs">
              <div>
                <div className="champ-label">Type</div>
                <div className="champ-valeur">
                  {libelleContrat(actif.type_contrat)}
                </div>
              </div>
              <div>
                <div className="champ-label">Organisme</div>
                <div className="champ-valeur">{actif.organisme || "—"}</div>
              </div>
              <div>
                <div className="champ-label">Début</div>
                <div className="champ-valeur">{formaterDate(actif.date_debut)}</div>
              </div>
              <div>
                <div className="champ-label">Fin</div>
                <div className="champ-valeur">
                  {formaterDate(actif.date_fin)}
                  {actif.jours_avant_fin !== null &&
                    actif.jours_avant_fin !== undefined &&
                    actif.jours_avant_fin <= 90 && (
                      <span className="urgence-retard">
                        {" "}
                        ·{" "}
                        {actif.jours_avant_fin < 0 ? "terminé depuis " : "dans "}
                        {ecartMoisJours(actif.date_fin)?.texte ??
                          `${Math.abs(actif.jours_avant_fin)} j`}
                      </span>
                    )}
                </div>
              </div>
              <div>
                <div className="champ-label">Loyer mensuel</div>
                <div className="champ-valeur">
                  {euros(actif.loyer_mensuel)} HT
                  {/* Le TTC est ce qui sort du compte chaque mois. Sans lui,
                      le client rapproche un HT d'un relevé bancaire TTC et
                      croit à une erreur de saisie. */}
                  {actif.loyer_mensuel_ttc !== null &&
                    actif.loyer_mensuel_ttc !== undefined && (
                      <span className="note-discrete">
                        {" "}· {euros(actif.loyer_mensuel_ttc)} TTC
                      </span>
                    )}
                </div>
              </div>
              <div>
                <div className="champ-label">Prix d’achat</div>
                <div className="champ-valeur">
                  {euros(actif.prix_achat)} HT
                  {actif.prix_achat_ttc !== null &&
                    actif.prix_achat_ttc !== undefined && (
                      <span className="note-discrete">
                        {" "}· TVA {euros(actif.prix_achat_tva)} ·{" "}
                        {euros(actif.prix_achat_ttc)} TTC
                      </span>
                    )}
                </div>
              </div>
              <div>
                <div className="champ-label">Valeur résiduelle</div>
                <div className="champ-valeur">
                  {/* Les DEUX : le pourcentage est ce qui figure au contrat,
                      les euros sont ce qu'il faudra sortir pour racheter le
                      camion. Obliger à l'un des deux calculs de tête, c'est
                      obliger à se tromper. */}
                  {actif.valeur_residuelle_pct !== null &&
                  actif.valeur_residuelle_pct !== undefined
                    ? `${Number(actif.valeur_residuelle_pct)} %`
                    : "—"}
                  {actif.valeur_residuelle_euros !== null &&
                    actif.valeur_residuelle_euros !== undefined && (
                      <span className="note-discrete">
                        {" "}· {euros(actif.valeur_residuelle_euros)}
                      </span>
                    )}
                </div>
              </div>
              <div>
                <div className="champ-label">Encours estimé</div>
                <div className="champ-valeur">
                  <strong>{euros(actif.encours_estime)}</strong>
                </div>
              </div>
              {actif.reference_contrat && (
                <div>
                  <div className="champ-label">Référence</div>
                  <div className="champ-valeur">{actif.reference_contrat}</div>
                </div>
              )}
            </div>
          )}

          {/* AVERTISSEMENT, PAS REFUS. Le serveur a accepté la saisie : un
              écart peut être légitime (apport, contrat renégocié, reprise
              déduite). On le signale sans bloquer — refuser une saisie exacte
              est pire que laisser passer une saisie douteuse signalée. */}
          {actif && actif.avertissement_coherence && (
            <p className="sous-info urgence-rappel">
              ⚠ {actif.avertissement_coherence}
            </p>
          )}

          {estAdmin && actif && (
            <div className="actions-form">
              {/* CORRIGER PLUTOT QUE SUPPRIMER ET RESSAISIR. Un contrat se
                  recopie d'un papier : on se trompe d'un chiffre ou d'un mois.
                  Sans ce bouton, la seule issue etait la suppression -- qui
                  efface aussi l'historique, et pousse a ne rien corriger. */}
              <button className="lien-action" onClick={() => corriger(actif)}>
                Modifier
              </button>
              <button className="lien-action" onClick={() => cloturer(actif)}>
                Clôturer le contrat
              </button>
              <button
                className="lien-action lien-danger"
                onClick={() => supprimer(actif)}
              >
                Supprimer
              </button>
            </div>
          )}

          {anciens.length > 0 && (
            <>
              <div className="champ-label" style={{ marginTop: 16 }}>
                Contrats terminés
              </div>
              <table className="tableau">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Organisme</th>
                    <th>Période</th>
                    <th>Loyer HT</th>
                    {estAdmin && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {anciens.map((c) => (
                    <tr key={c.id}>
                      <td>{libelleContrat(c.type_contrat)}</td>
                      <td>{c.organisme || "—"}</td>
                      <td>
                        {formaterDate(c.date_debut)} → {formaterDate(c.date_fin)}
                      </td>
                      <td>{euros(c.loyer_mensuel)}</td>
                      {estAdmin && (
                        <td className="actions-cellule">
                          <button
                            className="lien-action lien-danger"
                            onClick={() => supprimer(c)}
                          >
                            Supprimer
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      )}
    </section>
  );
}
